import { useState } from "react";
import { Button } from "antd";
import { X, Download } from "lucide-react";
import {
  ModusProvider,
  MicRecorderProvider,
  Converse,
  useModus,
  apiRequest,
} from "@vojomo-core/shared";

// ─── Types ───────────────────────────────────────────────────────────────────

interface AARFields {
  q1_accomplish?: string;
  q2_happened?: string;
  q3_why?: string;
  q4_start?: string;
}

interface AARVoPanelProps {
  sessionId: string;
  onExtract: (fields: AARFields) => void;
  onClose: () => void;
}

// ─── Inner panel (needs to be inside ModusProvider to access useModus) ───────

function AARVoPanelInner({
  sessionId,
  onExtract,
  onClose,
}: AARVoPanelProps) {
  const { modusChatContext, sendMessage } = useModus();
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState(false);

  const handleExtract = async () => {
    const messages = modusChatContext?.messages ?? [];
    if (messages.length < 2) return;

    setExtracting(true);
    const data = await apiRequest<{ fields: AARFields }>({
      subRoute: "aar",
      path: `/sessions/${sessionId}/vo-extract`,
      method: "POST",
      body: { conversation: messages },
    });
    setExtracting(false);

    if (data?.fields) {
      onExtract(data.fields);
      setExtracted(true);
    }
  };

  const onProcess = async (transcript: string) => {
    await sendMessage(transcript);
  };

  const messageCount =
    modusChatContext?.messages?.filter((m: { role: string }) => m.role === "user").length ?? 0;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-white shrink-0">
        <div>
          <p className="font-semibold text-sm text-purple-900 m-0">
            Talk with Vo
          </p>
          <p className="text-xs text-gray-400 m-0">
            Talk through your AAR — Vo will help capture your reflections
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors ml-3"
        >
          <X size={18} />
        </button>
      </div>

      {/* Chat area */}
      <div className="flex-1 overflow-hidden">
        <Converse size={275} onProcess={onProcess} showNewConversationButton />
      </div>

      {/* Extract footer */}
      <div className="px-4 py-3 border-t bg-gray-50 shrink-0 space-y-2">
        {extracted && (
          <p className="text-xs text-green-600 text-center m-0">
            ✓ Answers extracted to your form
          </p>
        )}
        <Button
          type="primary"
          icon={<Download size={14} />}
          onClick={handleExtract}
          loading={extracting}
          disabled={messageCount < 1}
          className="w-full"
          style={{ backgroundColor: "rgb(44, 21, 97)" }}
        >
          Extract answers to form
        </Button>
        <p className="text-xs text-gray-400 text-center leading-tight m-0">
          Vo will map what you've discussed to the 4 AAR fields
        </p>
      </div>
    </div>
  );
}

// ─── Public component (wraps with providers) ─────────────────────────────────

export function AARVoPanel(props: AARVoPanelProps) {
  return (
    <div
      className="fixed right-0 top-0 h-full w-96 bg-white border-l shadow-xl z-50 flex flex-col"
      style={{ maxWidth: "90vw" }}
    >
      <ModusProvider agentName="AARVoAgent">
        <MicRecorderProvider>
          <AARVoPanelInner {...props} />
        </MicRecorderProvider>
      </ModusProvider>
    </div>
  );
}
